import { Translator, Status, Vehicle, dummyTranslator, defaultStatus, defaultVehicle } from 'models'
import { loadingStatus } from 'models/dtos/subtypes'
import { keyMatchHelper } from 'utils/constants'
import React from 'react'
import { useGetStatusesTranslator } from 'utils/hooks/queries/useGetStatusesTranslator'
import { useAlert } from './AlertContext'

type StatusTranslatorContextType = {
  translator: Translator | undefined
  isTranslatorLoading: boolean
  isTranslatorError: boolean
  translateStatus: (status: Status) => Status
  translateStatusVehicle: (vehicle?: Vehicle) => Vehicle
  translateStatusVehicleArray: (array?: Array<Vehicle>) => Array<Vehicle>
  translateStatusByKey: (translateKey: string, valueToTranslate: string) => string
}
const StatusTranslatorContext = React.createContext<StatusTranslatorContextType>({
  translator: dummyTranslator,
  isTranslatorError: false,
  isTranslatorLoading: true,
  translateStatus: () => defaultStatus,
  translateStatusVehicle: () => defaultVehicle,
  translateStatusVehicleArray: () => [defaultVehicle],
  translateStatusByKey: () => '',
})

const TranslatorProvider = ({ children }: React.PropsWithChildren<unknown>) => {
  const { data, isLoading, isError } = useGetStatusesTranslator()

  const translator = React.useMemo(
    () => (isError || !data ? dummyTranslator : data),
    [data, isError],
  )

  const { displayAlert } = useAlert()

  React.useEffect(() => {
    if (isError) {
      displayAlert('Error fetching translator', 'error')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError])

  const translateStatus = (status?: Status) => {
    if (isLoading) return loadingStatus
    if (!status) return defaultStatus
    const newStatus = { ...status }

    Object.keys(status).forEach((typeOfStatus) => {
      if (typeOfStatus === 'blockingReason') return

      const numValueStringyfied = status[typeOfStatus as keyof Status]?.toString()

      const matchingKey = keyMatchHelper[typeOfStatus as keyof typeof keyMatchHelper]

      const matchingTranslationTable = translator.enums.find((value) => value.name === matchingKey)

      if (matchingTranslationTable) {
        const answer = matchingTranslationTable.values.find(
          (translation) => translation.value === numValueStringyfied,
        )?.title

        newStatus[typeOfStatus as keyof Status] = answer ?? status[typeOfStatus as keyof Status]
      }
    })

    return newStatus
  }

  const translateStatusVehicle = (vehicle?: Vehicle) => {
    if (!vehicle) {
      return defaultVehicle
    }
    if (!vehicle.status) {
      return { ...vehicle, status: defaultStatus }
    }
    const newStatus = translateStatus(vehicle.status)
    return { ...vehicle, status: newStatus }
  }

  const translateStatusVehicleArray = (array?: Array<Vehicle>) => {
    if (!array || array.length === 0) {
      return []
    }
    const result = array.map((line) => {
      return translateStatusVehicle(line)
    })
    return result
  }

  const translateStatusByKey = (translateKey: string, valueToTranslate: string) => {
    const targetEnumValues = translator?.enums.map((translateEnum) => {
      if (translateEnum.name === translateKey) {
        return translateEnum
      }
      return null
    })[0]?.values

    if (!targetEnumValues) {
      return valueToTranslate
    }
    const translation = targetEnumValues?.map((val) => {
      if (val.value === valueToTranslate) {
        return val.title
      }
      return null
    })[0]

    return translation || valueToTranslate
  }

  const providerValue = React.useMemo(
    () => ({
      translator,
      isTranslatorLoading: isLoading,
      isTranslatorError: isError,
      translateStatus,
      translateStatusVehicle,
      translateStatusVehicleArray,
      translateStatusByKey,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [translator, isError, isLoading],
  )

  return (
    <StatusTranslatorContext.Provider value={providerValue}>
      {children}
    </StatusTranslatorContext.Provider>
  )
}

const useTranslator = () => React.useContext(StatusTranslatorContext)

export { StatusTranslatorContext, TranslatorProvider, useTranslator }
